import { FC, useEffect, useRef, useState } from "react";
import { retriveBooking } from "api/booking/booking";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { payTargetFor } from "utils/payTarget";
import { dollars, payByText } from "utils/reservationUnpaid";

/** How often, and how many times, the notice asks for the payment link the backend makes after the booking. */
export const LINK_POLL_MS = 3000;
export const LINK_POLL_TRIES = 12;
/** How long "Pay and book" waits for the browser to leave for the payment page before it says it could not. */
export const OPEN_WAIT_MS = 5000;

/**
 * 1NOW-11476: what an unpaid pay-later reservation shows on top of its booking page: what is due and by when,
 * and Pay and book, which opens the booking's own payment link (the one the email and the text carry). The backend
 * makes that link after the booking is saved, so it may not be on the first
 * read: the notice reads the booking again a few times meanwhile and hands each read to the page. When the browser
 * does not leave (1NOW-11206) it says so and offers the same link as a plain link.
 */
const UnpaidNotice: FC<{ booking: any; total: string; onBooking: (booking: any) => void }> = ({
  booking,
  total,
  onBooking,
}) => {
  const target = payTargetFor(booking);
  const id = booking?.unique_identifier;
  const hasTarget = Boolean(target);
  const [gaveUp, setGaveUp] = useState(false);
  const [opening, setOpening] = useState(false);
  const [stuck, setStuck] = useState(false);
  const giveUpOpening = useRef<number>();

  useEffect(() => {
    if (!id || hasTarget) return;
    let live = true;
    let tries = 0;
    let timer: number | undefined;
    const look = async () => {
      // The call is typed { data }, but answers with the booking itself (or { error, status }).
      const read: any = await retriveBooking(id);
      if (!live) return;
      tries += 1;
      if (read && !read.error && read.id != null) {
        onBooking(read);
        if (payTargetFor(read)) return;
      }
      if (tries >= LINK_POLL_TRIES) return setGaveUp(true);
      timer = window.setTimeout(look, LINK_POLL_MS);
    };
    timer = window.setTimeout(look, LINK_POLL_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [id, hasTarget]);

  // Back here from the payment page (the browser kept this page): a fresh Pay and book, not a stuck one.
  useEffect(() => {
    const back = () => {
      window.clearTimeout(giveUpOpening.current);
      setOpening(false);
      setStuck(false);
    };
    window.addEventListener("pageshow", back);
    return () => {
      window.removeEventListener("pageshow", back);
      window.clearTimeout(giveUpOpening.current);
    };
  }, []);

  const pay = () => {
    if (!target || opening) return;
    setStuck(false);
    setOpening(true);
    window.clearTimeout(giveUpOpening.current);
    const fail = () => {
      setOpening(false);
      setStuck(true);
    };
    giveUpOpening.current = window.setTimeout(fail, OPEN_WAIT_MS);
    try {
      window.location.assign(target.url);
    } catch {
      window.clearTimeout(giveUpOpening.current);
      fail();
    }
  };

  const amount = target?.amount != null ? dollars(target.amount) : total;
  const due = payByText(booking);
  const label = !target && !gaveUp ? "Preparing your link" : opening ? "Opening payment…" : "Pay and book";

  return (
    <div className="w-full rounded-lg border border-neutral-200 bg-bg-color p-5 text-text-color" data-testid="unpaid-reservation-notice">
      <p className="text-lg font-extrabold">This booking isn&apos;t paid yet</p>
      <p className="mt-1 text-sm opacity-80" data-testid="amount-due">
        Pay {amount}
        {due ? ` by ${due}` : ""} to confirm it. If it isn&apos;t paid by then, the reservation is released. After you
        pay, you can confirm your ID and insurance here.
      </p>
      {gaveUp && !target && (
        <p className="mt-3 text-xs font-semibold">Your link is not ready on this page yet. Use the one we emailed and texted you.</p>
      )}
      {stuck && target && (
        <p role="alert" className="mt-3 text-xs font-semibold" data-testid="pay-stuck">
          We couldn&apos;t open the payment page.{" "}
          <a href={target.url} target="_blank" rel="noopener noreferrer" className="underline">
            Open it here
          </a>
          , or use the link we emailed and texted you.
        </p>
      )}
      <ButtonPrimary
        type="button"
        onClick={pay}
        disabled={!target || opening}
        className="mt-4 w-full sm:w-auto"
      >
        {label}
      </ButtonPrimary>
    </div>
  );
};

export default UnpaidNotice;
